/** @typedef {import('../../generic/utilities/objectStructures/balanced/BalancedData').BalancedData} BalancedData */
/** @typedef {import('../../generic/utilities/objectStructures/balanced/DeviceGroupDevice').DeviceGroupDevice} DeviceGroupDevice */

import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiGet } from '../../generic/Api_Functions'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import { COMPACT_SELECT_SX } from '../../generic/utilities/objectStructures/balanced/balancedStyles'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * Reads a group out of whatever the state holds for it (a number after a user choice, the
 * `Integer` the backend sent after a load). A group is an integer >= 1: `0` is not a group.
 *
 * @param {*} value
 * @returns {number|null}
 */
const toGroup = (value) => {
  const group = parseInt(value, 10)
  return Number.isInteger(group) && group >= 1 ? group : null
}

/**
 * Number of positions of a line, which is its number of devices — never the highest
 * `positionInLine` of those devices.
 *
 * That is exactly what the backend counts when it publishes the position-in-line types
 * (`DeviceGroupRest.getDeviceGroupDevices().size()`), so deriving it here from the line catalogue
 * offers the very same numbers with one request less. Lines whose devices have gaps, duplicates or
 * null positions are shown as they are: this screen does not normalise nor validate them.
 *
 * @param {Object} line - Line as served by `deviceGroups/lines`, or the `{id, name}` of a saved one
 * @param {Object.<number, number>} deviceCountByLineId - Counts learned from `deviceGroupDevices/{id}`
 * @returns {number|null} The group, or `null` when the line has no devices or its size is not known yet
 */
const positionsOf = (line, deviceCountByLineId) => {
  if (!line) return null
  const count = Array.isArray(line.devices) ? line.devices.length : deviceCountByLineId[line.id]
  return Number.isInteger(count) && count >= 1 ? count : null
}

/**
 * The group a saved balancing belongs to: the position count in generic mode, the line's device
 * count in line mode. Anything else — no mode, no positions, no line, a non-positive count — has
 * no group, which is what blocks the save until the user picks one.
 *
 * @param {Object|null} workflow - Loaded workflow, with its persisted balancing
 * @param {Object.<number, number>} deviceCountByLineId
 * @returns {number|null}
 */
const savedGroupOf = (workflow, deviceCountByLineId) => {
  if (!workflow) return null
  if (workflow.balancedType === 'TYPE') return toGroup(workflow.positions)
  if (workflow.balancedType === 'LINE') return positionsOf(workflow.deviceGroup, deviceCountByLineId)
  return null
}

/**
 * Filter bar rendered below the page header.
 *
 * The two selects are the two levels of a single cascade: the first one (mandatory) says how many
 * positions the line being balanced has, and the second one (optional) narrows it down to one of
 * the lines of that size. Picking only the group leaves the generic balancing (`TYPE`, columns
 * `1..N`); adding a line ties it to that line (`LINE`, columns = its devices). Neither mode is
 * chosen directly any more: both are the outcome of the cascade.
 *
 * @param {Object}        props
 * @param {Function}      props.dispatchBalancedData - Reducer dispatcher from `Balanced`
 * @param {BalancedData}  props.balancedData         - Shared balanced state from `Balanced`
 * @param {boolean}       props.disabled             - When true all controls are read-only
 * @param {Function}      props.onDevicesLoaded      - Called with DeviceGroupDevice[] when a line is selected
 * @param {Function}      props.onRequestChange      - Wraps any change action; may show a confirmation dialog
 * @param {Function}      props.onGroupResolved      - Reports the group in force (or `null` when it is not derivable yet)
 */
const BalancedFormHeader = ({ dispatchBalancedData, balancedData, disabled, onDevicesLoaded, onRequestChange, onGroupResolved }) => {
  const { t_ } = useSmartTranslation()
  const [lines, setLines] = useState([])
  // Sizes learned from `deviceGroupDevices/{id}`: the only way to know the group of a line that
  // the active catalogue does not carry — a deactivated one, or one still in flight.
  const [deviceCountByLineId, setDeviceCountByLineId] = useState({})

  // Loads the available lines once on mount. The position-in-line types are no longer requested:
  // every group is derived from the lines this same response already carries.
  useEffect(() => {
    const loadLines = async () => {
      // Both catalogues on screen are derived from this one list, so a request that fails or that
      // answers something other than a list (the `undefined` a lost connection returns while it
      // redirects) leaves the selects empty instead of taking the screen down with it.
      const data = await apiGet('deviceGroups/lines').catch(() => [])
      setLines(Array.isArray(data) ? data : [])
    }
    loadLines()
  }, [])

  // Auto-loads devices whenever the selected line changes (including on initial load).
  useEffect(() => {
    if (balancedData.balancedType !== 'LINE' || !balancedData.selectedType?.id) {
      onDevicesLoaded?.([], null)
      return
    }
    const lineId = balancedData.selectedType.id
    const loadDevices = async () => {
      // A failed read leaves the columns and the group exactly as they were: the line stays shown
      // and its size stays unknown, which is the same thing as not having arrived yet.
      const devices = await apiGet('deviceGroupDevices/' + lineId).catch(() => null)
      if (!Array.isArray(devices)) return
      setDeviceCountByLineId((known) => ({ ...known, [lineId]: devices.length }))
      onDevicesLoaded?.(devices, lineId)
    }
    loadDevices()
  }, [balancedData.balancedType, balancedData.selectedType?.id, onDevicesLoaded])

  const savedLine = balancedData.workflow?.balancedType === 'LINE' ? balancedData.workflow.deviceGroup : null
  const savedLineId = savedLine?.id ?? null

  // The line the loaded workflow was balanced against joins the catalogue when the active one does
  // not carry it (it was deactivated): otherwise the screen would show a balancing tied to a line
  // its own select claims not to have. It stays offered while that workflow is loaded, so clearing
  // it is undoable; loading another workflow recomputes everything.
  const catalogueLines = useMemo(() => {
    if (!savedLineId || lines.some((line) => line.id === savedLineId)) return lines
    return [...lines, savedLine]
  }, [lines, savedLine, savedLineId])

  const groupOf = useCallback((line) => positionsOf(line, deviceCountByLineId), [deviceCountByLineId])

  // The group in force: what the first select shows. In line mode it is the size of the selected
  // line, which arrives asynchronously — until then there is no group, and that is not the same
  // as having none (nothing is flagged, nothing is blocked by the empty control alone).
  const currentGroup = balancedData.balancedType === 'TYPE'
    ? toGroup(balancedData.selectedType)
    : balancedData.balancedType === 'LINE'
      ? groupOf(balancedData.selectedType)
      : null

  const savedGroup = savedGroupOf(balancedData.workflow, deviceCountByLineId)

  const groupOptions = useMemo(() => {
    const groups = new Set()
    catalogueLines.forEach((line) => {
      const group = groupOf(line)
      if (group) groups.add(group)
    })
    // Opening a workflow never drops information: its own group is offered even when no active
    // line has that size any more, in its numeric place.
    if (savedGroup) groups.add(savedGroup)
    if (currentGroup) groups.add(currentGroup)
    return [...groups].sort((a, b) => a - b).map(String)
  }, [catalogueLines, groupOf, savedGroup, currentGroup])

  const candidateLines = currentGroup === null
    ? []
    : catalogueLines.filter((line) => groupOf(line) === currentGroup)

  // `N positions`, or `1 position`: the number plus the literal that already existed for both.
  const groupLabel = useCallback(
    (group) => `${group} ${t_(parseInt(group, 10) === 1 ? 'Position' : 'Positions').toLocaleLowerCase()}`,
    [t_]
  )

  // The save guard lives in `Balanced`, which cannot tell a line's size on its own.
  useEffect(() => {
    onGroupResolved?.(currentGroup)
  }, [currentGroup, onGroupResolved])

  /**
   * Picks the position group: the top level of the cascade.
   *
   * Unlike the mode select it replaces, this IS a context change — it fixes `positions` and
   * therefore rebuilds the columns — so it goes through `onRequestChange`, asking for confirmation
   * only when the new group has fewer positions than the one in force. That comparison is made in
   * both modes: reading it off `selectedType` alone would yield `NaN` while tied to a line and
   * would skip the confirmation in the very case that unassigns work.
   *
   * @param {string} value - Selected option: the group as text
   * @returns {void}
   */
  const handleGroupChange = (value) => {
    const group = toGroup(value)
    // Re-picking the group in force is not a change: it must not clear the line, ask, or remap.
    if (group === null || group === currentGroup) return
    const risky = currentGroup !== null && group < currentGroup
    onRequestChange(() => {
      dispatchBalancedData({ type: 'SET_BALANCED_TYPE', balancedType: 'TYPE' })
      dispatchBalancedData({ type: 'SET_BALANCED_OPTION', selectedType: group })
    }, risky)
  }

  /**
   * Picks the concrete line, or clears it. Choosing a line ties the balancing to it, exactly as
   * before. Clearing it goes back to the generic balancing OF THE SAME GROUP — one step down the
   * cascade, not two — and is applied directly: `LINE → TYPE` is the most conservative remap there
   * is, and the non-blocking unassigned warning is the net.
   *
   * @param {string|null} value - Selected line name, or `null` when the user cleared the control
   * @returns {void}
   */
  const handleLineChange = (value) => {
    if (value === null || value === undefined) {
      if (balancedData.balancedType !== 'LINE') return
      const group = currentGroup
      onRequestChange(() => {
        dispatchBalancedData({ type: 'SET_BALANCED_TYPE', balancedType: 'TYPE' })
        dispatchBalancedData({ type: 'SET_BALANCED_OPTION', selectedType: group })
      }, false)
      return
    }
    const line = candidateLines.find((candidate) => candidate.name === value)
    if (!line) return
    // A different line may unassign jobs whose position/technology no longer fit.
    onRequestChange(() => {
      dispatchBalancedData({ type: 'SET_BALANCED_TYPE', balancedType: 'LINE' })
      dispatchBalancedData({ type: 'SET_BALANCED_OPTION', selectedType: line })
    }, true)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.3rem' }}>
      <Typography variant='caption' color='primary' style={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0 }}>
        {t_('Balanced')}
      </Typography>
      <Box sx={COMPACT_SELECT_SX}>
        <SmartSelect
          label={t_('Line type')}
          selectableOptions={groupOptions}
          value={currentGroup === null ? null : String(currentGroup)}
          onChange={(value) => handleGroupChange(value)}
          optionLabelTransform={groupLabel}
          // Only the deferred red of a blocked save, and only while there is still no group: the
          // control says nothing until the user has actually tried to save, and it stops saying it
          // the moment the group shows up — also when it shows up on its own, once the line's
          // devices arrive. `required` (a permanent asterisk) is left off on purpose.
          error={(balancedData.balancedTypeError ?? false) && currentGroup === null}
          // The groups are ordered by their number: left to the component they would be sorted
          // alphabetically by the translated label, which puts 25 between 2 and 3.
          sortValues={false}
          size='small'
          minWidth='10rem'
          width='10rem'
          disabled={disabled}
          autoSelectFirst={false}
        />
      </Box>
      <Box sx={COMPACT_SELECT_SX}>
        <SmartSelect
          label={t_('Line')}
          selectableOptions={candidateLines.map(line => line.name)}
          value={balancedData.balancedType === 'LINE' ? balancedData.selectedType?.name ?? null : null}
          onChange={(value) => handleLineChange(value)}
          // Optional level of the cascade: it can be emptied, which returns to the generic
          // balancing of the group in force.
          disableClearable={false}
          size='small'
          minWidth='12rem'
          width='12rem'
          disabled={disabled}
          autoSelectFirst={false}
        />
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={balancedData.balancedApproved ?? false}
            onChange={(e) => dispatchBalancedData({ type: 'SET_BALANCED_APPROVED', balancedApproved: e.target.checked })}
            disabled={disabled}
            size='small'
          />
          }
        label={<Typography variant='body2'>{t_('Approved')}</Typography>}
      />
    </div>
  )
}

export default BalancedFormHeader
